import { ContractWithDetails } from '../../types/contracts';
import { ShieldCheck, CheckCircle2, Clock, Globe } from 'lucide-react';
import { format } from 'date-fns';

interface ContractCertificateProps {
  contract: ContractWithDetails;
}

const ROLE_LABELS: Record<string, string> = {
  buyer: 'Buyer',
  seller: 'Seller',
  agent: 'Agent',
};

export function ContractCertificate({ contract }: ContractCertificateProps) {
  const { signatures } = contract;
  const transactionId = signatures[0]?.transaction_id || `TXN-${contract.id.substring(0, 8).toUpperCase()}`;
  const executedAt = signatures.length > 0
    ? format(new Date(signatures[signatures.length - 1].signed_at), 'MMMM d, yyyy \'at\' h:mm:ss a z')
    : null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Certificate */}
      <div
        id="contract-certificate"
        className="bg-[var(--t-surface)] border-2 border-[var(--t-primary)] rounded-3xl p-10 text-center shadow-2xl shadow-[var(--t-primary-dim)]"
        style={{ background: 'linear-gradient(135deg, var(--t-surface) 0%, var(--t-surface-hover) 100%)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[var(--t-primary-dim)] flex items-center justify-center">
            <ShieldCheck size={28} className="text-[var(--t-primary)]" />
          </div>
        </div>

        <h2 className="text-2xl font-black uppercase tracking-widest text-[var(--t-primary)] mb-1">
          Certificate of Completion
        </h2>
        <p className="text-sm text-[var(--t-text-muted)] mb-8">Electronic Signature Verification</p>

        <div className="py-6 border-t border-b border-[var(--t-border)] mb-8">
          <p className="text-base text-[var(--t-text-muted)] mb-2">This certifies that the following contract has been fully executed:</p>
          <h3 className="text-xl font-bold text-[var(--t-text)]">{contract.title}</h3>
          {executedAt && (
            <p className="text-sm text-[var(--t-text-muted)] mt-1 flex items-center justify-center gap-1">
              <Clock size={13} /> Completed on {executedAt}
            </p>
          )}
        </div>

        {/* Transaction ID */}
        <div className="mb-8 p-4 bg-[var(--t-bg)] rounded-2xl border border-[var(--t-border)] inline-block mx-auto">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--t-text-muted)] mb-1">Transaction ID</p>
          <p className="font-mono font-bold text-[var(--t-primary)] text-sm">{transactionId}</p>
        </div>

        {/* Signers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 text-left">
          {signatures.map((sig, idx) => (
            <div
              key={sig.id}
              className="bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl p-5 space-y-3"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[var(--t-success-dim)] flex items-center justify-center">
                  <CheckCircle2 size={16} className="text-[var(--t-success)]" />
                </div>
                <div>
                  <p className="font-bold text-[var(--t-text)] text-sm">{sig.signer_name}</p>
                  <p className="text-xs text-[var(--t-text-muted)] uppercase tracking-wide">{ROLE_LABELS[sig.signer_type] || sig.signer_type}</p>
                </div>
              </div>

              <div className="space-y-1.5 text-xs text-[var(--t-text-muted)]">
                <div className="flex items-center gap-1.5">
                  <Clock size={11} />
                  <span>{format(new Date(sig.signed_at), 'MMM d, yyyy h:mm a')}</span>
                </div>
                {sig.ip_address && (
                  <div className="flex items-center gap-1.5">
                    <Globe size={11} />
                    <span>IP: {sig.ip_address}</span>
                  </div>
                )}
                {sig.consent_given && (
                  <div className="flex items-center gap-1.5 text-[var(--t-success)]">
                    <CheckCircle2 size={11} />
                    <span>Electronic consent confirmed</span>
                  </div>
                )}
              </div>

              {/* Signature image */}
              {sig.signature_data && (
                <div className="border border-[var(--t-border)] rounded-xl overflow-hidden bg-white p-2">
                  <img src={sig.signature_data} alt={`${sig.signer_name} signature`} className="max-h-16 w-full object-contain" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <p className="text-xs text-[var(--t-text-muted)] leading-relaxed">
          This certificate confirms that all parties have electronically signed the above-referenced contract
          through WholeScale OS's secure e-signature platform. Electronic signatures are legally binding
          under the Electronic Signatures in Global and National Commerce Act (ESIGN) and the Uniform
          Electronic Transactions Act (UETA).
        </p>
      </div>

      {/* Print button */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-6 py-2.5 bg-[var(--t-surface)] text-[var(--t-text)] border border-[var(--t-border)] text-sm font-semibold rounded-xl hover:bg-[var(--t-surface-hover)] transition-colors"
        >
          🖨️ Print / Save as PDF
        </button>
      </div>
    </div>
  );
}
